import React, { useState } from "react";
import { Link } from "react-router-dom";

import api from "../Utils/api";
import logo from "../Assets/logo.png";
import Button from "../Components/Button";

export default function ForgotPassword() {
  const [step, setStep] = useState("username"); // 'username', 'email', 'success'
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleUsernameSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.requestPasswordReset(username);

      if (response.emailRequired) {
        // User doesn't have an email, need to collect it
        setStep("email");
      } else if (response.emailSent) {
        // Email sent successfully
        setStep("success");
      }
    } catch (err) {
      setError(err.message || "Failed to process request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // First update the email
      await api.updateEmailForReset(username, email);

      // Then retry the password reset request
      const response = await api.requestPasswordReset(username);

      if (response.emailSent) {
        setStep("success");
      }
    } catch (err) {
      setError(err.message || "Failed to process request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full bg-primary p-4">
      <div className="w-full max-w-md flex flex-col items-center">
        {/* Back to Login Button */}
        <Link
          to="/auth"
          className="self-start mb-4 flex items-center text-gray-400 hover:text-white transition-colors group"
        >
          <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Login
        </Link>

        {/* Card Container */}
        <div className="w-full bg-secondary border border-neutral-800 rounded-md shadow-2xl p-8 md:p-10 animate-scale-in">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src={logo}
              alt="Muzio Logo"
              className="w-24 h-24 md:w-32 md:h-32 hover:scale-105 transition-transform"
            />
          </div>

          {step === "username" && (
            <>
              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
                Reset Password
              </h1>
              <p className="text-gray-400 text-center mb-8">
                Enter your username to receive a password reset link
              </p>

              {/* Form */}
              <form onSubmit={handleUsernameSubmit} className="space-y-5">
                {/* Error Message */}
                {error && (
                  <div className="bg-accent bg-opacity-20 border border-accent text-white px-4 py-3 rounded-lg animate-slide-down">
                    <p className="text-sm text-center">{error}</p>
                  </div>
                )}

                {/* Username Input */}
                <div className="space-y-2">
                  <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                    Username
                  </label>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    autoFocus
                    className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {isLoading ? "Processing..." : "Continue"}
                </Button>
              </form>
            </>
          )}

          {step === "email" && (
            <>
              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
                Email Required
              </h1>
              <p className="text-gray-400 text-center mb-8">
                Please provide your email address to receive the reset link
              </p>

              {/* Form */}
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                {/* Error Message */}
                {error && (
                  <div className="bg-accent bg-opacity-20 border border-accent text-white px-4 py-3 rounded-lg animate-slide-down">
                    <p className="text-sm text-center">{error}</p>
                  </div>
                )}

                {/* Info Message */}
                <div className="bg-blue-900 bg-opacity-30 border border-blue-600 text-blue-300 px-4 py-3 rounded-lg">
                  <p className="text-sm text-center">
                    Your account doesn't have an email associated with it yet. We'll save it for future use.
                  </p>
                </div>

                {/* Email Input */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-md text-white placeholder-text-dim focus:outline-none focus:border-accent transition-colors"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {isLoading ? "Processing..." : "Send Reset Link"}
                </Button>
              </form>
            </>
          )}

          {step === "success" && (
            <>
              {/* Success Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-green-900 bg-opacity-30 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
                Check Your Email
              </h1>
              <p className="text-gray-400 text-center mb-8">
                We've sent a password reset link to your email address. The link will expire in 1 hour.
              </p>

              {/* Additional Info */}
              <div className="bg-neutral-700 border border-neutral-700 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-300 text-center">
                  Didn't receive the email? Check your spam folder or try requesting a new link.
                </p>
              </div>

              {/* Back to Login Button */}
              <Link to="/auth">
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  Back to Login
                </Button>
              </Link>
            </>
          )}

          {/* Back to Login Link */}
          {step !== "success" && (
            <p className="text-center text-gray-400 mt-6">
              Remember your password?{" "}
              <Link
                to="/auth"
                className="text-accent hover:text-accent font-medium transition-colors"
              >
                Sign in
              </Link>
            </p>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          © 2024 Muzio. All rights reserved.
        </p>
      </div>
    </div>
  );
}
